/**
 * MHR Protocol Setup Page
 *
 * Create or edit a protocol with its basic details.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ListChecks, Copy, Trash2, AlertCircle } from 'lucide-react';
import { useMHR } from '../contexts';
import { ProtocolForm } from '../components/protocol/ProtocolForm';
import { ProtocolStatusBadge } from '../components/protocol/ProtocolStatusBadge';
import { Breadcrumb } from '../components/Breadcrumb';
import { Toast } from '../components/Toast';
import * as mhrApi from '../lib/api';
import type { Protocol, ProtocolFormData, ProtocolStats } from '../types';

export function ProtocolSetup() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const navigate = useNavigate();
  const { loadProtocols, refreshProtocol } = useMHR();

  const isNew = !protocolId;

  // State
  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [stats, setStats] = useState<ProtocolStats | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Clear success message handler
  const clearSuccess = useCallback(() => setSuccess(null), []);

  // Load protocol if editing
  useEffect(() => {
    if (!protocolId) return;

    const loadProtocolData = async () => {
      setLoading(true);
      try {
        const [protocolData, statsData] = await Promise.all([
          mhrApi.getProtocol(protocolId),
          mhrApi.getProtocolStats(protocolId),
        ]);
        setProtocol(protocolData);
        setStats(statsData);
      } catch (err: any) {
        console.error('[ProtocolSetup] Error loading protocol:', err);
        setError(err.response?.data?.error || 'Failed to load protocol');
      } finally {
        setLoading(false);
      }
    };

    loadProtocolData();
  }, [protocolId]);

  const handleSubmit = async (data: ProtocolFormData) => {
    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        const newProtocol = await mhrApi.createProtocol(data);
        await loadProtocols(true);
        setSuccess('Protocol created successfully');
        navigate(`/mhr/protocols/${newProtocol.id}`);
      } else {
        await mhrApi.updateProtocol(protocolId!, data);
        await refreshProtocol(protocolId!);
        setProtocol(prev => (prev ? { ...prev, ...data } : null));
        setSuccess('Protocol saved successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save protocol');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!protocolId) return;

    setSaving(true);
    try {
      const newProtocol = await mhrApi.duplicateProtocol(protocolId);
      await loadProtocols(true);
      setSuccess('Protocol duplicated successfully');
      navigate(`/mhr/protocols/${newProtocol.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to duplicate protocol');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!protocolId) return;

    setSaving(true);
    try {
      await mhrApi.closeProtocol(protocolId);
      await loadProtocols(true);
      navigate('/mhr');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close protocol');
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/mhr' },
    ...(protocol
      ? [{ label: protocol.protocol_number, href: `/mhr/protocols/${protocolId}` }]
      : []),
    { label: isNew ? 'New Protocol' : 'Setup' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/mhr"
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
              {isNew ? 'New Protocol' : 'Edit Protocol'}
            </h1>
            {protocol && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-600 dark:text-gray-400">{protocol.protocol_number}</span>
                <ProtocolStatusBadge status={protocol.status} />
              </div>
            )}
          </div>
        </div>

        {/* Actions for existing protocol */}
        {!isNew && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleDuplicate}
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
              title="Duplicate protocol"
            >
              <Copy size={18} />
              <span className="hidden sm:inline">Duplicate</span>
            </button>
            {protocol?.status !== 'closed' && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
                title="Close protocol"
              >
                <Trash2 size={18} />
                <span className="hidden sm:inline">Close</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Close Protocol?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to close this protocol? Closed protocols cannot accept new
              reviews, but existing reviews will be preserved.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
              >
                {saving ? 'Closing...' : 'Close Protocol'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Protocol Details</h2>
            <ProtocolForm
              protocol={protocol}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/mhr')}
              isLoading={saving}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Criteria link */}
          {!isNew && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                  <ListChecks className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">Criteria</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {stats?.criteria.total || 0} total criteria
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  {stats?.criteria.inclusion || 0} inclusion
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  {stats?.criteria.exclusion || 0} exclusion
                </span>
              </div>

              <Link
                to={`/mhr/protocols/${protocolId}/criteria`}
                className="inline-flex items-center gap-2 w-full justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
              >
                <ListChecks size={18} />
                Manage Criteria
              </Link>
            </div>
          )}

          {/* Stats */}
          {!isNew && stats && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Review Statistics</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Reviews</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{stats.reviews.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Completed</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{stats.reviews.completed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Pending</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{stats.reviews.pending}</span>
                </div>
                {stats.reviews.completed > 0 && (
                  <>
                    <hr className="border-gray-100 dark:border-gray-700" />
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>Eligible</span>
                      <span className="font-medium">{stats.eligibility.eligible}</span>
                    </div>
                    <div className="flex justify-between text-red-600 dark:text-red-400">
                      <span>Excluded</span>
                      <span className="font-medium">{stats.eligibility.excluded}</span>
                    </div>
                    <div className="flex justify-between text-amber-600 dark:text-amber-400">
                      <span>Review Required</span>
                      <span className="font-medium">{stats.eligibility.review_required}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Help */}
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
            <h3 className="font-medium text-primary-900 dark:text-primary-300 mb-2">
              {isNew ? 'Getting Started' : 'Next Steps'}
            </h3>
            <p className="text-sm text-primary-700 dark:text-primary-400">
              {isNew
                ? 'Create a protocol to start defining inclusion and exclusion criteria for patient screening.'
                : 'Add criteria to this protocol, then start reviewing patient medical histories against these criteria.'}
            </p>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      <Toast message={success} type="success" onClose={clearSuccess} />
    </div>
  );
}
